import { cn } from "@gettactic/helpers";
import React from "react";
import { FormControlWrapper } from "../FormControlWrapper";
import {
  useFormControlInputA11y,
  useFormControlWrapper
} from "../FormControlWrapper/FormControlWrapperProvider";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import {
  TextareaInputProps,
  TextareaInputPropsWithFormControl
} from "./TextareaInput.types";

const disabledCssClasses = "cursor-not-allowed opacity-50";

export const TextareaInputInput = React.forwardRef<
  HTMLTextAreaElement,
  TextareaInputProps
>((props, ref) => {
  const { inputClassName, className, disableOutline, ...rest } = props;
  const {
    id,
    isDisabled,
    isReadOnly,
    maxLength,
    minLength,
    setCharactersCounter
  } = useFormControlWrapper();

  const a11yProps = useFormControlInputA11y({
    "aria-invalid": rest["aria-invalid"] === true || undefined,
    "aria-required": rest["aria-required"] === true || undefined,
    "aria-describedby": rest["aria-describedby"]
  });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharactersCounter?.(e.target.value.length || 0);
    rest.onChange?.(e);
  };

  return (
    <textarea
      {...rest}
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-input placeholder:text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive",
        (isDisabled || isReadOnly) && disabledCssClasses,
        disableOutline && "outline-none ring-0",
        className,
        inputClassName
      )}
      {...a11yProps}
      ref={ref}
      id={id}
      disabled={isDisabled || rest.disabled}
      maxLength={maxLength}
      minLength={minLength}
      onChange={handleChange}
      readOnly={isReadOnly || rest.readOnly}
      tabIndex={isReadOnly ? -1 : rest.tabIndex}
    />
  );
});

TextareaInputInput.displayName = "TextareaInputInput";

/**
 * A multi-line text field with label, messages, character counter and validation states. Inside
 * react-hook-form use `HookFormTextareaInput`.
 */
export const TextareaInput = React.forwardRef<
  HTMLTextAreaElement,
  TextareaInputPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <FormControlWrapper variant={"unstyled"} {...wrapperProps}>
      <TextareaInputInput ref={ref} {...inputProps} />
    </FormControlWrapper>
  );
});

TextareaInput.displayName = "TextareaInput";
